import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState, useCallback } from "react"
import { useEditor, EditorContent, Extension } from '@tiptap/react'
import { Bold as BoldExtension } from '@tiptap/extension-bold'
import { Document as DocumentExtension } from '@tiptap/extension-document'
import { Link as LinkExtension } from '@tiptap/extension-link'
import { Placeholder as PlaceholderExtension } from '@tiptap/extension-placeholder'
import { Underline as UnderlineExtension } from '@tiptap/extension-underline'
import { Paragraph as ParagraphExtension } from '@tiptap/extension-paragraph'
import { OrderedList as OrderedListExtension } from '@tiptap/extension-ordered-list'
import { BulletList as BulletListExtension } from '@tiptap/extension-bullet-list'
import { ListItem as ListItemExtension } from '@tiptap/extension-list-item'
import { Text as TextExtension } from '@tiptap/extension-text'
import { Italic as ItalicExtension } from '@tiptap/extension-italic'
import { Dropcursor as DropcursorExtension } from '@tiptap/extension-dropcursor'
import { Gapcursor as GapcursorExtension } from '@tiptap/extension-gapcursor'
import { HardBreak as HardBreakExtension } from '@tiptap/extension-hard-break'
import { History as HistoryExtension } from '@tiptap/extension-history'
import { useTranslation } from "react-i18next"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Popover, PopoverTrigger, PopoverContent } from "./popover"
import { Send, SquarePen, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, AtSign, Paperclip, Smile, AlertCircle } from 'lucide-react'

// Common emojis organized by category
const emojiData = {
  "Smileys": ["😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇", "🙂", "🙃", "😉", "😌", "😍", "🥰", "😘", "😗", "😙", "😚", "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", "😎", "🤩", "🥳"],
  "Gestures": ["👍", "👎", "👌", "✌️", "🤞", "🤟", "🤘", "🤙", "👈", "👉", "👆", "🖕", "👇", "☝️", "👋", "🤚", "🖐️", "✋", "🖖", "👏", "🙌", "🤲", "🙏", "✍️", "💪", "🦵", "🦶", "👂", "🦻", "👃", "🧠"],
  "Objects": ["⌚", "📱", "📲", "💻", "⌨️", "🖥️", "🖨️", "🖱️", "🖲️", "🕹️", "🗜️", "💽", "💾", "💿", "📀", "📼", "📷", "📸", "📹", "🎥", "📽️", "🎞️", "📞", "☎️", "📟", "📠", "📺", "📻", "🎙️", "🎚️", "🎛️"],
  "Symbols": ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❣️", "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💟", "☮️", "✝️", "☪️", "🕉️", "☸️", "✡️", "🔯", "🕎", "☯️", "☦️", "🛐", "⛎"]
}

// Custom extension for keyboard shortcuts
const KeyboardShortcuts = Extension.create({
  name: 'keyboardShortcuts',

  addKeyboardShortcuts() {
    return {
      'Mod-Enter': ({ editor }) => {
        // Trigger send event through a custom event
        const event = new CustomEvent('tiptap-send', { detail: { editor } })
        document.dispatchEvent(event)
        return true
      },
    }
  },
})

export interface TiptapHtmlEditorProps {
  value?: string
  onChange?: (value: string) => void
  onSend?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
  showSendButton?: boolean
  isImportant?: boolean
  onImportanceChange?: (isImportant: boolean) => void
}

export interface TiptapHtmlEditorRef {
  focus: () => void
  clear: () => void
  getValue: () => string
  instance: () => any
  getImportance: () => boolean
  setImportance: (isImportant: boolean) => void
}

const TiptapHtmlEditor = forwardRef<TiptapHtmlEditorRef, TiptapHtmlEditorProps>(
  ({
    value = "",
    onChange,
    onSend,
    placeholder,
    disabled = false,
    className,
    showSendButton = false,
    isImportant = false,
    onImportanceChange
  }, ref) => {
    const { t } = useTranslation()

    // Streamlined interface state
    const [showToolbar, setShowToolbar] = useState(false)
    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const [showLinkDialog, setShowLinkDialog] = useState(false)
    const [linkUrl, setLinkUrl] = useState('')
    const [savedSelection, setSavedSelection] = useState<{ from: number, to: number, text?: string } | null>(null)
    const [internalIsImportant, setInternalIsImportant] = useState(isImportant)

    // Sync internal importance state with prop
    useEffect(() => {
      setInternalIsImportant(isImportant)
    }, [isImportant])

    // Initialize Tiptap editor
    const editor = useEditor({
      extensions: [
        BoldExtension,
        BulletListExtension.configure({
          keepMarks: true,
          keepAttributes: false,
        }),
        DocumentExtension,
        DropcursorExtension,
        GapcursorExtension,
        HardBreakExtension,
        HistoryExtension,
        ItalicExtension,
        ListItemExtension,
        LinkExtension.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
        OrderedListExtension.configure({
          keepMarks: true,
          keepAttributes: false,
        }),
        ParagraphExtension,
        TextExtension,
        UnderlineExtension,
        PlaceholderExtension.configure({
          placeholder: placeholder || '',
        }),
        KeyboardShortcuts,
      ],
      content: value,
      editable: !disabled,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()
        onChange?.(html)
      },
      onCreate: ({ editor }) => {
        // Set initial content if provided
        if (value && value !== editor.getHTML()) {
          editor.commands.setContent(value)
        }
      },
    })

    // Update editor content when value prop changes
    useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        editor.commands.setContent(value)
      }
    }, [editor, value])

    // Update editor editable state when disabled prop changes
    useEffect(() => {
      if (editor) {
        editor.setEditable(!disabled)
      }
    }, [editor, disabled])

    /**
     * Keep the placeholder in step with the prop.
     *
     * Tiptap configures extensions once, at editor creation, so the placeholder froze at whatever the
     * panel passed on first mount — the Internal tab's wording — and went on saying "Write an internal
     * update…" while the Client tab was selected. Mutating the extension option and dispatching an
     * empty transaction re-renders the decoration without tearing the editor down and losing whatever
     * has been typed.
     */
    useEffect(() => {
      if (!editor) return

      const extension = editor.extensionManager.extensions.find(item => item.name === 'placeholder')
      if (!extension || extension.options.placeholder === (placeholder ?? '')) return

      extension.options.placeholder = placeholder ?? ''
      editor.view.dispatch(editor.state.tr)
    }, [editor, placeholder])

    // Handle send keyboard shortcut
    useEffect(() => {
      const handleSendEvent = (event: CustomEvent) => {
        if (event.detail.editor === editor && onSend) {
          const content = editor?.getHTML() ?? ''
          if (content.trim() && content !== '<p></p>') {
            onSend()
          }
        }
      }

      document.addEventListener('tiptap-send', handleSendEvent as EventListener)
      return () => {
        document.removeEventListener('tiptap-send', handleSendEvent as EventListener)
      }
    }, [editor, onSend])

    useImperativeHandle(ref, () => ({
      focus: () => {
        editor?.commands.focus()
      },
      clear: () => {
        editor?.commands.clearContent()
        onChange?.("")
      },
      getValue: () => {
        return editor?.getHTML() || ""
      },
      instance: () => editor,
      getImportance: () => internalIsImportant,
      setImportance: (isImportant: boolean) => {
        setInternalIsImportant(isImportant)
        onImportanceChange?.(isImportant)
      }
    }))

    // Handle send button click
    const handleSend = useCallback(() => {
      if (onSend && editor) {
        const content = editor.getHTML()
        if (content.trim() && content !== '<p></p>') {
          onSend()
        }
      }
    }, [onSend, editor])

    // Handle importance toggle
    const handleImportanceToggle = useCallback(() => {
      const newImportance = !internalIsImportant
      setInternalIsImportant(newImportance)
      onImportanceChange?.(newImportance)
    }, [internalIsImportant, onImportanceChange])

    // Handle input row click - focus the editor
    const handleInputRowClick = useCallback((e: React.MouseEvent) => {
      // Don't focus if clicking on buttons
      if ((e.target as Element).closest('.tiptap-html-editor-buttons')) {
        return
      }
      editor?.commands.focus()
    }, [editor])

    // Handle toolbar actions
    const handleToolbarAction = useCallback((action: string) => {
      if (!editor) return

      switch (action) {
        case 'bold':
          editor.chain().focus().toggleBold().run()
          break
        case 'italic':
          editor.chain().focus().toggleItalic().run()
          break
        case 'underline':
          editor.chain().focus().toggleUnderline().run()
          break
        case 'insertOrderedList':
          editor.chain().focus().toggleOrderedList().run()
          break
        case 'insertUnorderedList':
          editor.chain().focus().toggleBulletList().run()
          break
        case 'link':
          {
            // Save current selection before opening dialog
            const { from, to } = editor.state.selection
            const selectedText = editor.state.doc.textBetween(from, to)
            setSavedSelection({ from, to, text: selectedText })
            setShowLinkDialog(true)
            break
          }
      }
    }, [editor])

    const saveSelection = useCallback((open: boolean) => {
      if (open && editor) {
        const { from, to } = editor.state.selection
        const selectedText = editor.state.doc.textBetween(from, to)
        setSavedSelection({ from, to, text: selectedText })
      }
      setShowLinkDialog(open)
    }, [editor])

    // Handle emoji selection
    const handleEmojiSelect = useCallback((emoji: string) => {
      if (editor) {
        editor.chain().focus().insertContent(emoji).run()
      }
      setShowEmojiPicker(false)
    }, [editor])

    const isSafeUrl = (url: string): boolean => {
      try {
        const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : '')
        const allowed = ['http:', 'https:', 'mailto:', 'tel:']
        return allowed.includes(u.protocol)
      } catch {
        return false
      }
    }

    // Handle link insertion
    const handleLinkInsert = useCallback(() => {
      const trimmed = linkUrl.trim()
      if (editor && trimmed && savedSelection && isSafeUrl(trimmed)) {
        if (savedSelection.to > savedSelection.from) {
          // Apply link to previously selected text
          editor
            .chain()
            .focus()
            .setTextSelection({ from: savedSelection.from, to: savedSelection.to })
            .setLink({ href: trimmed })
            .run()
        } else {
          // Insert new link with URL as text
          editor
            .chain()
            .focus()
            .insertContentAt(savedSelection.from, `<a href="${trimmed}">${trimmed}</a>`)
            .run()
        }
      }
      setShowLinkDialog(false)
      setLinkUrl('')
      setSavedSelection(null)
    }, [editor, linkUrl, savedSelection])

    // Handle link dialog cancel
    const handleLinkCancel = useCallback(() => {
      setShowLinkDialog(false)
      setLinkUrl('')
      if (editor && savedSelection) {
        editor.commands.setTextSelection({ from: savedSelection.from, to: savedSelection.to })
        editor.commands.focus()
      }
      setSavedSelection(null)
    }, [editor, savedSelection])

    // Handle escape key globally for popups
    const handleEscapeKey = useCallback((e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showLinkDialog) {
          e.preventDefault()
          e.stopPropagation()
          handleLinkCancel()
          return
        }
        if (showEmojiPicker) {
          e.preventDefault()
          e.stopPropagation()
          setShowEmojiPicker(false)
          editor?.commands.focus()
          return
        }
      }
    }, [showLinkDialog, showEmojiPicker, handleLinkCancel, editor])

    // Add escape key listener
    useEffect(() => {
      document.addEventListener('keydown', handleEscapeKey, true)
      return () => {
        document.removeEventListener('keydown', handleEscapeKey, true)
      }
    }, [handleEscapeKey])

    // Cleanup editor on unmount
    useEffect(() => {
      return () => {
        editor?.destroy()
      }
    }, [editor])

    if (!editor) {
      return null
    }

    return (
      <div
        className={cn("tiptap-html-editor-wrapper", className)}
        data-disabled={disabled}
      >
        <div className="tiptap-html-editor-container">
          {/* Expandable toolbar - appears above input */}
          {showToolbar && (
            <div className="tiptap-html-editor-toolbar">
              <div className="tiptap-html-editor-toolbar-buttons">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToolbarAction('bold')}
                  title={t('tooltips.bold')}
                  className={cn(editor.isActive('bold') && 'bg-muted')}
                  aria-label={t('tooltips.bold')}
                  aria-pressed={editor.isActive('bold')}
                >
                  <Bold size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToolbarAction('italic')}
                  title={t('tooltips.italic')}
                  className={cn(editor.isActive('italic') && 'bg-muted')}
                  aria-label={t('tooltips.italic')}
                  aria-pressed={editor.isActive('italic')}
                >
                  <Italic size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToolbarAction('underline')}
                  title={t('tooltips.underline')}
                  className={cn(editor.isActive('underline') && 'bg-muted')}
                  aria-label={t('tooltips.underline')}
                  aria-pressed={editor.isActive('underline')}
                >
                  <UnderlineIcon size={16} />
                </Button>
                <div className="tiptap-html-editor-toolbar-separator" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToolbarAction('insertOrderedList')}
                  title={t('tooltips.numberedList')}
                  className={cn(editor.isActive('orderedList') && 'bg-muted')}
                  aria-label={t('tooltips.numberedList')}
                  aria-pressed={editor.isActive('orderedList')}
                >
                  <ListOrdered size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToolbarAction('insertUnorderedList')}
                  title={t('tooltips.bulletList')}
                  className={cn(editor.isActive('bulletList') && 'bg-muted')}
                  aria-label={t('tooltips.bulletList')}
                  aria-pressed={editor.isActive('bulletList')}
                >
                  <List size={16} />
                </Button>
                <div className="tiptap-html-editor-toolbar-separator" />
                <Popover open={showLinkDialog} onOpenChange={saveSelection}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      title={t('tooltips.link')}
                      className={cn(editor.isActive('link') && 'bg-muted')}
                      aria-label={t('tooltips.link')}
                      aria-pressed={editor.isActive('link')}
                    >
                      <Link2 size={16} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="entity-chat-panel-link-popover"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault()
                        e.stopPropagation()
                        handleLinkCancel()
                      }
                    }}
                  >
                    <div className="entity-chat-panel-link-header">
                      <div className="entity-chat-panel-link-title">{t('tooltips.addLink')}</div>
                    </div>
                    <div className="entity-chat-panel-link-content">
                      {savedSelection && savedSelection.to > savedSelection.from && (
                        <div className="entity-chat-panel-link-preview">
                          <span className="entity-chat-panel-link-preview-label">
                            {t('tooltips.linkText')}:
                          </span>
                          <span className="entity-chat-panel-link-preview-text">
                            &quot;{savedSelection.text || ''}&quot;
                          </span>
                        </div>
                      )}
                      <input
                        type="url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder={t('tooltips.enterUrl')}
                        className="entity-chat-panel-link-input"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            e.stopPropagation()
                            handleLinkInsert()
                          } else if (e.key === 'Escape') {
                            e.preventDefault()
                            e.stopPropagation()
                            handleLinkCancel()
                          }
                        }}
                        autoFocus
                      />
                      <div className="entity-chat-panel-link-actions">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleLinkCancel}
                          aria-label={t('messages.cancel')}
                        >
                          {t('messages.cancel')}
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleLinkInsert}
                          disabled={!linkUrl.trim()}
                          aria-label={t('tooltips.addLink')}
                        >
                          {t('tooltips.addLink')}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <div className="tiptap-html-editor-toolbar-separator" />
                <Button
                  variant="ghost"
                  size="sm"
                  disabled
                  title={t('tooltips.mentions')}
                  aria-label={t('tooltips.mentions')}
                >
                  <AtSign size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled
                  title={t('tooltips.attachments')}
                  aria-label={t('tooltips.attachments')}
                >
                  <Paperclip size={16} />
                </Button>
              </div>
            </div>
          )}

          {/* Main input row */}
          <div className="tiptap-html-editor-input-row" onClick={handleInputRowClick}>
            <div className="tiptap-html-editor-input-wrapper">
              <EditorContent
                editor={editor}
                className="tiptap-html-editor-content"
              />
            </div>

            {/* Inline buttons */}
            <div className="tiptap-html-editor-buttons">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleImportanceToggle}
                className={cn(
                  "tiptap-html-editor-button",
                  "tiptap-html-editor-importance-button",
                  internalIsImportant && "tiptap-html-editor-importance-active"
                )}
                title={internalIsImportant ? t('tooltips.markAsNormal') : t('tooltips.markAsImportant')}
                disabled={disabled}
              >
                <span
                  className={cn(
                    "entity-chat-importance-button-icon",
                    internalIsImportant && "active"
                  )}
                >
                  !
                </span>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowToolbar(!showToolbar)}
                className={cn("tiptap-html-editor-button", "tiptap-html-editor-format-button")}
                title={showToolbar ? t('tooltips.hideFormatting') : t('tooltips.showFormatting')}
                disabled={disabled}
              >
                <SquarePen size={16} />
              </Button>

              <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn("tiptap-html-editor-button", "tiptap-html-editor-emoji-button")}
                    title={t('tooltips.insertEmoji')}
                    disabled={disabled}
                  >
                    <Smile size={16} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="entity-chat-panel-emoji-popover"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault()
                      e.stopPropagation()
                      setShowEmojiPicker(false)
                      editor?.commands.focus()
                    }
                  }}
                >
                  <div className="entity-chat-panel-emoji-header">
                    <div className="entity-chat-panel-emoji-title">{t('emoji.popoverTitle')}</div>
                  </div>
                  <div className="entity-chat-panel-emoji-grid-container">
                    <div className="entity-chat-panel-emoji-grid">
                      {Object.values(emojiData).flat().map((emoji, index) => (
                        <Button
                          key={index}
                          variant="ghost"
                          size="sm"
                          className="entity-chat-panel-emoji-item"
                          onClick={() => handleEmojiSelect(emoji)}
                        >
                          {emoji}
                        </Button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {showSendButton && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSend}
                  className="tiptap-html-editor-send-button"
                  disabled={disabled || !editor.getText().trim()}
                  title={t('tooltips.sendMessage')}
                >
                  <Send size={16} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
)

TiptapHtmlEditor.displayName = "TiptapHtmlEditor"

// Memoize the component to prevent unnecessary re-renders
const MemoizedTiptapHtmlEditor = React.memo(TiptapHtmlEditor)

export { MemoizedTiptapHtmlEditor as TiptapHtmlEditor }
